import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Plus,
} from 'lucide-react';
import { Button, Badge, Modal, Input, Select, EmptyState } from '@/components/ui';
import { AppointmentModal } from '@/components/features/AppointmentModal';
import { appointmentService } from '@/services/appointmentService';
import { resourceService } from '@/services/resourceService';
import type { Appointment, AppointmentStatus } from '@/types/appointment';
import styles from './SchedulePage.module.css';

/** Timeline runs from 07:00 to 20:00. */
const TIMELINE_START_HOUR = 7;
const TIMELINE_END_HOUR = 20;
const SLOT_HEIGHT_PX = 60;

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  SCHEDULED: 'Agendado',
  CONFIRMED: 'Confirmado',
  WAITING: 'Aguardando',
  IN_PROGRESS: 'Em Atendimento',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  NO_SHOW: 'Não Compareceu',
};

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  SCHEDULED: '#4A90D9',
  CONFIRMED: '#00A651',
  WAITING: '#F7941D',
  IN_PROGRESS: '#7B61FF',
  COMPLETED: '#6B7280',
  CANCELLED: '#DC2626',
  NO_SHOW: '#DC2626',
};

const STATUS_STYLE_MAP: Record<AppointmentStatus, string> = {
  SCHEDULED: styles.statusScheduled,
  CONFIRMED: styles.statusConfirmed,
  WAITING: styles.statusWaiting,
  IN_PROGRESS: styles.statusInProgress,
  COMPLETED: styles.statusCompleted,
  CANCELLED: styles.statusCancelled,
  NO_SHOW: styles.statusNoShow,
};

const BADGE_VARIANT_MAP: Record<AppointmentStatus, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
  SCHEDULED: 'info',
  CONFIRMED: 'success',
  WAITING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'neutral',
  CANCELLED: 'error',
  NO_SHOW: 'error',
};

/**
 * Formats a date string as "weekday, DD/MM/YYYY" in pt-BR.
 */
function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Returns today's date as YYYY-MM-DD string.
 */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Adds days to a date string and returns new YYYY-MM-DD string.
 */
function addDays(dateStr: string, days: number): string {
  const date = new Date(dateStr + 'T12:00:00');
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converts "HH:MM" time string to minutes since midnight.
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Generates time slot labels from start to end hour.
 */
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
  }
  return slots;
}

interface ResourceColumn {
  id: string;
  name: string;
  appointments: Appointment[];
}

/**
 * Admin page displaying a day-view calendar schedule.
 *
 * Features:
 * - Multi-resource column view (all resources side by side)
 * - Single resource focused view
 * - Timeline from 07:00 to 20:00 with half-hour markers
 * - Summary cards with appointment counts
 * - Current time indicator
 * - Color-coded appointment blocks by status
 * - Click appointments to view details and trigger actions
 * - Status legend
 */
export function SchedulePage() {
  const queryClient = useQueryClient();

  const [selectedDate, setSelectedDate] = useState(getTodayString);
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);

  const timeSlots = useMemo(() => generateTimeSlots(), []);

  // Fetch resources for filter and column headers
  const { data: resources = [] } = useQuery({
    queryKey: ['resources', { isActive: true }],
    queryFn: () => resourceService.getResources({ isActive: true }),
  });

  const resourceOptions = useMemo(
    () => [
      { value: '', label: 'Todos os recursos' },
      ...resources.map((r) => ({ value: r.id, label: r.name })),
    ],
    [resources],
  );

  // Fetch daily schedule for single resource
  const {
    data: dailySchedule,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['daily-schedule', selectedResourceId, selectedDate],
    queryFn: () => appointmentService.getDailySchedule(selectedResourceId, selectedDate),
    enabled: selectedResourceId !== '',
  });

  // Fetch dashboard when no specific resource is selected
  const { data: dashboard, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['clinic-dashboard', selectedDate],
    queryFn: () => appointmentService.getClinicDashboard(selectedDate),
    enabled: selectedResourceId === '',
  });

  const appointments = useMemo(() => {
    if (selectedResourceId && dailySchedule) {
      return dailySchedule.appointments ?? [];
    }
    if (!selectedResourceId && dashboard) {
      return dashboard.appointments ?? [];
    }
    return [];
  }, [selectedResourceId, dailySchedule, dashboard]);

  const isLoadingData = selectedResourceId ? isLoading : isDashboardLoading;

  // Group appointments into resource columns
  const resourceColumns = useMemo((): ResourceColumn[] => {
    if (selectedResourceId) {
      const resource = resources.find((r) => r.id === selectedResourceId);
      return [
        {
          id: selectedResourceId,
          name: resource?.name ?? 'Recurso',
          appointments,
        },
      ];
    }

    // Multi-resource mode: create a column per active resource
    const columns: ResourceColumn[] = resources.map((r) => ({
      id: r.id,
      name: r.name,
      appointments: [],
    }));

    const columnMap = new Map(columns.map((c) => [c.id, c]));

    appointments.forEach((appt) => {
      const col = columnMap.get(appt.resourceId);
      if (col) {
        col.appointments.push(appt);
      } else {
        // Appointment for a resource not in the active list
        const newCol: ResourceColumn = {
          id: appt.resourceId,
          name: appt.resourceName,
          appointments: [appt],
        };
        columns.push(newCol);
        columnMap.set(appt.resourceId, newCol);
      }
    });

    if (columns.length === 0) {
      return [{ id: '', name: 'Agenda', appointments }];
    }

    return columns;
  }, [appointments, selectedResourceId, resources]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!selectedResourceId && dashboard) {
      return {
        total:
          (dashboard.totalScheduled ?? 0) +
          (dashboard.totalConfirmed ?? 0) +
          (dashboard.totalWaiting ?? 0) +
          (dashboard.totalInProgress ?? 0) +
          (dashboard.totalCompleted ?? 0) +
          (dashboard.totalCancelled ?? 0) +
          (dashboard.totalNoShow ?? 0),
        scheduled: dashboard.totalScheduled ?? 0,
        confirmed: dashboard.totalConfirmed ?? 0,
        waiting: dashboard.totalWaiting ?? 0,
        inProgress: dashboard.totalInProgress ?? 0,
        completed: dashboard.totalCompleted ?? 0,
      };
    }

    const counts: Record<string, number> = {};
    appointments.forEach((a) => {
      counts[a.status] = (counts[a.status] || 0) + 1;
    });

    return {
      total: appointments.length,
      scheduled: counts['SCHEDULED'] ?? 0,
      confirmed: counts['CONFIRMED'] ?? 0,
      waiting: counts['WAITING'] ?? 0,
      inProgress: counts['IN_PROGRESS'] ?? 0,
      completed: counts['COMPLETED'] ?? 0,
    };
  }, [appointments, dashboard, selectedResourceId]);

  // Current time indicator position
  const [currentTimeTop, setCurrentTimeTop] = useState<number | null>(null);

  useEffect(() => {
    if (selectedDate !== getTodayString()) {
      setCurrentTimeTop(null);
      return;
    }

    const update = () => {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      const start = TIMELINE_START_HOUR * 60;
      const end = TIMELINE_END_HOUR * 60;

      if (minutes >= start && minutes <= end) {
        setCurrentTimeTop((minutes - start) * (SLOT_HEIGHT_PX / 60));
      } else {
        setCurrentTimeTop(null);
      }
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  // Status transition mutations
  const confirmMutation = useMutation({
    mutationFn: (id: string) => appointmentService.confirm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
    },
  });

  const checkInMutation = useMutation({
    mutationFn: (id: string) => appointmentService.checkIn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
    },
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => appointmentService.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => appointmentService.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (id: string) => appointmentService.markNoShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      appointmentService.cancel(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setSelectedAppointment(null);
      setIsCancelModalOpen(false);
      setCancelReason('');
    },
  });

  const handlePreviousDay = useCallback(() => {
    setSelectedDate((prev) => addDays(prev, -1));
  }, []);

  const handleNextDay = useCallback(() => {
    setSelectedDate((prev) => addDays(prev, 1));
  }, []);

  const handleToday = useCallback(() => {
    setSelectedDate(getTodayString());
  }, []);

  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setSelectedDate(e.target.value);
    }
  }, []);

  const handleAppointmentClick = useCallback((appointment: Appointment) => {
    setSelectedAppointment(appointment);
  }, []);

  const handleOpenCancelModal = useCallback(() => {
    setCancelReason('');
    setIsCancelModalOpen(true);
  }, []);

  const handleCancelSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedAppointment || !cancelReason.trim()) return;
      cancelMutation.mutate({ id: selectedAppointment.id, reason: cancelReason.trim() });
    },
    [selectedAppointment, cancelReason, cancelMutation],
  );

  /**
   * Calculates top offset and height for an appointment block
   * based on its start/end times relative to the timeline.
   */
  const getBlockPosition = useCallback((appointment: Appointment) => {
    const timelineStartMinutes = TIMELINE_START_HOUR * 60;
    const startMinutes = timeToMinutes(appointment.startTime);
    const endMinutes = timeToMinutes(appointment.endTime);
    const minutesPerSlot = 60;
    const pixelsPerMinute = SLOT_HEIGHT_PX / minutesPerSlot;

    const top = (startMinutes - timelineStartMinutes) * pixelsPerMinute;
    const height = Math.max((endMinutes - startMinutes) * pixelsPerMinute, 20);

    return { top, height };
  }, []);

  const isAnyMutationPending =
    confirmMutation.isPending ||
    checkInMutation.isPending ||
    startMutation.isPending ||
    completeMutation.isPending ||
    noShowMutation.isPending ||
    cancelMutation.isPending;

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar agenda. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  const summaryCards = [
    { label: 'Total', value: summaryStats.total, className: styles.summaryTotal },
    { label: 'Agendados', value: summaryStats.scheduled, className: styles.summaryScheduled },
    { label: 'Confirmados', value: summaryStats.confirmed, className: styles.summaryConfirmed },
    { label: 'Aguardando', value: summaryStats.waiting, className: styles.summaryWaiting },
    { label: 'Em Atendimento', value: summaryStats.inProgress, className: styles.summaryInProgress },
    { label: 'Concluídos', value: summaryStats.completed, className: styles.summaryCompleted },
  ];

  const showMultiColumnHeaders = resourceColumns.length > 1;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Agenda</h1>
          <p className={styles.subtitle}>Visualize e gerencie os agendamentos do dia.</p>
        </div>
        <Button
          leftIcon={<Plus size={18} />}
          onClick={() => setIsAppointmentModalOpen(true)}
        >
          Novo Agendamento
        </Button>
      </header>

      {/* Summary Cards */}
      <div className={styles.summaryRow}>
        {summaryCards.map((card) => (
          <div key={card.label} className={`${styles.summaryCard} ${card.className}`}>
            <span className={styles.summaryValue}>{card.value}</span>
            <span className={styles.summaryLabel}>{card.label}</span>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.dateNavGroup}>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousDay}
            aria-label="Dia anterior"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleToday}>
            Hoje
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextDay}
            aria-label="Próximo dia"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </Button>
          <span className={styles.dateDisplay} aria-live="polite">
            {formatDateDisplay(selectedDate)}
          </span>
        </div>

        <div className={styles.toolbarRight}>
          <Input
            label="Data"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            leftIcon={<Calendar size={16} />}
          />
          <div className={styles.resourceFilter}>
            <Select
              label="Recurso"
              options={resourceOptions}
              value={selectedResourceId}
              onChange={(e) => setSelectedResourceId(e.target.value)}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Timeline */}
      {isLoadingData ? (
        <div className={styles.timelineWrapper}>
          <div className={styles.loadingState} role="status" aria-busy="true">
            <p>Carregando agenda...</p>
          </div>
        </div>
      ) : (
        <div className={styles.timelineWrapper}>
          {/* Resource column headers (multi-resource view) */}
          {showMultiColumnHeaders && (
            <div className={styles.resourceHeaders}>
              <div className={styles.timeGutterHeader} />
              {resourceColumns.map((col) => (
                <div key={col.id} className={styles.resourceHeaderCell}>
                  {col.name}
                </div>
              ))}
            </div>
          )}

          {/* Timeline body */}
          <div
            className={styles.timelineBody}
            role="grid"
            aria-label={`Agenda do dia ${formatDateDisplay(selectedDate)}`}
          >
            {/* Time gutter */}
            <div className={styles.timeGutter} role="rowheader">
              {timeSlots.map((slot) => (
                <div key={slot} className={styles.timeCell}>
                  <span className={styles.timeLabel}>{slot}</span>
                </div>
              ))}
            </div>

            {/* Resource columns */}
            {resourceColumns.map((col) => (
              <div key={col.id} className={styles.resourceColumn} role="gridcell">
                {/* Hour cells with half-hour markers */}
                {timeSlots.map((slot) => (
                  <div key={slot} className={styles.hourCell}>
                    <div className={styles.halfHourLine} />
                  </div>
                ))}

                {/* Appointment blocks */}
                {col.appointments.map((appt) => {
                  const { top, height } = getBlockPosition(appt);
                  const statusClass = STATUS_STYLE_MAP[appt.status];

                  return (
                    <button
                      key={appt.id}
                      type="button"
                      className={`${styles.appointmentBlock} ${statusClass}`}
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                      }}
                      onClick={() => handleAppointmentClick(appt)}
                      aria-label={`${appt.patientName}, ${appt.procedureTypeName}, ${appt.startTime} - ${appt.endTime}, ${STATUS_LABELS[appt.status]}`}
                    >
                      <div className={styles.blockPatient}>{appt.patientName}</div>
                      {height > 30 && (
                        <div className={styles.blockProcedure}>{appt.procedureTypeName}</div>
                      )}
                      {height > 45 && (
                        <div className={styles.blockTime}>
                          {appt.startTime} - {appt.endTime}
                        </div>
                      )}
                    </button>
                  );
                })}

                {/* Current time indicator */}
                {currentTimeTop !== null && (
                  <div
                    className={styles.currentTimeLine}
                    style={{ top: `${currentTimeTop}px` }}
                    aria-hidden="true"
                  >
                    <div className={styles.currentTimeDot} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Empty state overlay */}
          {appointments.length === 0 && (
            <div className={styles.emptyTimeline}>
              <EmptyState
                icon={<Calendar size={40} />}
                title="Nenhum agendamento"
                description={`Não há agendamentos para ${formatDateDisplay(selectedDate)}${selectedResourceId ? ' neste recurso' : ''}.`}
                action={
                  <Button
                    size="sm"
                    leftIcon={<Plus size={16} />}
                    onClick={() => setIsAppointmentModalOpen(true)}
                  >
                    Novo Agendamento
                  </Button>
                }
              />
            </div>
          )}

          {/* Status legend */}
          <div className={styles.legend} aria-label="Legenda de status">
            {(Object.keys(STATUS_LABELS) as AppointmentStatus[]).map((status) => (
              <div key={status} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                  aria-hidden="true"
                />
                <span>{STATUS_LABELS[status]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointment detail modal */}
      <Modal
        isOpen={selectedAppointment !== null && !isCancelModalOpen}
        onClose={() => setSelectedAppointment(null)}
        title="Detalhes do Agendamento"
        size="md"
      >
        {selectedAppointment && (
          <div className={styles.detailPanel}>
            <div className={styles.detailGrid}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Paciente</span>
                <span className={styles.detailValue}>{selectedAppointment.patientName}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Procedimento</span>
                <span className={styles.detailValue}>
                  {selectedAppointment.procedureTypeName}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Recurso</span>
                <span className={styles.detailValue}>{selectedAppointment.resourceName}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Horário</span>
                <span className={styles.detailValue}>
                  {selectedAppointment.startTime} - {selectedAppointment.endTime}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Tipo</span>
                <span className={styles.detailValue}>{selectedAppointment.appointmentType}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status</span>
                <Badge variant={BADGE_VARIANT_MAP[selectedAppointment.status]}>
                  {STATUS_LABELS[selectedAppointment.status]}
                </Badge>
              </div>
            </div>
            {selectedAppointment.notes && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Observações</span>
                <span className={styles.detailValue}>{selectedAppointment.notes}</span>
              </div>
            )}
            {selectedAppointment.cancellationReason && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Motivo do cancelamento</span>
                <span className={styles.detailValue}>
                  {selectedAppointment.cancellationReason}
                </span>
              </div>
            )}
            <div className={styles.detailActions}>
              {selectedAppointment.status === 'SCHEDULED' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => confirmMutation.mutate(selectedAppointment.id)}
                    disabled={isAnyMutationPending}
                    isLoading={confirmMutation.isPending}
                  >
                    Confirmar
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleOpenCancelModal}
                    disabled={isAnyMutationPending}
                  >
                    Cancelar
                  </Button>
                </>
              )}
              {selectedAppointment.status === 'CONFIRMED' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => checkInMutation.mutate(selectedAppointment.id)}
                    disabled={isAnyMutationPending}
                    isLoading={checkInMutation.isPending}
                  >
                    Check-in
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleOpenCancelModal}
                    disabled={isAnyMutationPending}
                  >
                    Cancelar
                  </Button>
                </>
              )}
              {selectedAppointment.status === 'WAITING' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => startMutation.mutate(selectedAppointment.id)}
                    disabled={isAnyMutationPending}
                    isLoading={startMutation.isPending}
                  >
                    Iniciar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => noShowMutation.mutate(selectedAppointment.id)}
                    disabled={isAnyMutationPending}
                    isLoading={noShowMutation.isPending}
                  >
                    Não Compareceu
                  </Button>
                </>
              )}
              {selectedAppointment.status === 'IN_PROGRESS' && (
                <Button
                  size="sm"
                  onClick={() => completeMutation.mutate(selectedAppointment.id)}
                  disabled={isAnyMutationPending}
                  isLoading={completeMutation.isPending}
                >
                  Concluir
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title="Cancelar Agendamento"
        size="sm"
      >
        <form onSubmit={handleCancelSubmit} className={styles.cancelForm} noValidate>
          <Input
            label="Motivo do cancelamento"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Informe o motivo"
            required
            fullWidth
          />
          <div className={styles.cancelFormActions}>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setIsCancelModalOpen(false)}
            >
              Voltar
            </Button>
            <Button
              variant="danger"
              type="submit"
              disabled={!cancelReason.trim()}
              isLoading={cancelMutation.isPending}
            >
              Confirmar Cancelamento
            </Button>
          </div>
        </form>
      </Modal>

      {/* Appointment creation wizard */}
      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
          queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
        }}
        initialDate={selectedDate}
        initialResourceId={selectedResourceId || undefined}
      />
    </div>
  );
}
